import {styled} from "@mui/material/styles"
import {Typography, Box} from "@mui/material"
import {useTranslation} from "react-i18next"

import {PaymentOptionSection} from "./PaymentOptionSection"
import {OffersOptionSection} from "./OffersOptionSection"

import {colors, gradients} from "@indebted/theme/colors"
import {HorizontalContentPadding, Page} from "@indebted/components/Page"
import {IndebtedLogo, LockIcon} from "@indebted/assets"
import {AppNewThemeProvider} from "@indebted/theme/AppTheme"
import {CustomerFindPayload} from "@indebted/api/customer/customerFind"
import {IndebtedLogoWithYear} from "@indebted/components/IndebtedLogoWithYear"

function OutstandingBalance({
	PaymentOptions,
	StatuteOfLimitationsDisclosure,
	DisplayLicenceNumberText,
	CustomerName,
	OutstandingBalance,
	Currency,
	ClientName,
	ShowFooter,
	LicenceNumber,
}: CustomerFindPayload) {
	const {t} = useTranslation()
	return (
		<AppNewThemeProvider>
			<MainContainer>
				<HeroWrapper>
					<HeroHeader>
						<Box>
							<Typography variant="subtitle2">
								{t("customer_get.outstanding_balance.hello", {CustomerName: CustomerName})}
							</Typography>
							<Typography variant="subtitle2">
								{t("customer_get.outstanding_balance.solution_text")}
							</Typography>
						</Box>
						<IndebtedLogo alt="InDebted" width={41} />
					</HeroHeader>

					<BalanceText>
						<Typography
							variant="subtitle1"
							color={colors.endlessGalaxy["100"]}
							sx={{lineHeight: "1.5rem", opacity: 0.7, paddingBottom: "0.5rem"}}
						>
							{t("customer_get.outstanding_balance.outstanding_balance_label")}
						</Typography>
						<Typography variant="h3" fontWeight={700} sx={{fontSize: "36px", lineHeight: "44px"}}>
							{t("customer_get.outstanding_balance.outstanding_balance_value", {
								OutstandingBalanceValue: OutstandingBalance / 100,
								formatParams: {OutstandingBalanceValue: {currency: Currency}},
							})}
						</Typography>
						<Divider />
						<Typography variant="subtitle1">
							{t("customer_get.outstanding_balance.on_behalf_of", {ClientName: ClientName})}
						</Typography>
					</BalanceText>
				</HeroWrapper>

				<Container>
					<OffersOptionSection PaymentOptions={PaymentOptions} Currency={Currency} />
					<PaymentOptionSection PaymentOptions={PaymentOptions} />
				</Container>

				<Footer>
					<IndebtedLogoWithYear />

					<Box mt={2}>
						{ShowFooter && (
							<Typography
								variant="body2"
								color={colors.heron["900"]}
								textAlign="start"
								className="amp-mask"
							>
								{t("customer_get.outstanding_balance.footnote", {ClientName: ClientName})}
							</Typography>
						)}
						{DisplayLicenceNumberText && (
							<Typography
								variant="body2"
								color={colors.heron["900"]}
								textAlign="start"
								className="amp-mask"
							>
								{t("customer_get.outstanding_balance.licence_number_text", {
									LicenceNumber: LicenceNumber,
								})}
							</Typography>
						)}
						{StatuteOfLimitationsDisclosure && (
							<Typography
								variant="body2"
								textAlign="start"
								color={colors.heron["900"]}
								style={{whiteSpace: "pre-line"}}
							>
								{StatuteOfLimitationsDisclosure}
							</Typography>
						)}
						<Box sx={{display: "flex", gap: "0.5rem", paddingTop: "2.19rem", alignItems: "center"}}>
							<LockIcon width={24} height={24} style={{verticalAlign: "middle"}} />
							<Typography
								variant="caption"
								color={colors.heron["900"]}
								sx={{
									fontSize: "0.9375rem",
									fontWeight: 400,
									lineHeight: "normal",
									verticalAlign: "middle",
								}}
							>
								{t("customer_get.outstanding_balance.security_message")}
							</Typography>
						</Box>
					</Box>
				</Footer>
			</MainContainer>
		</AppNewThemeProvider>
	)
}

const MainContainer = styled("div")`
	background-color: ${colors.purpleIllusion["100"]};
	font-family: "Manrope", sans-serif;
	margin: 0 auto;
	padding: 0rem;
	flex-direction: column;
	min-height: 100vh;
	display: flex;
`
const Container = styled(Page)`
	background-color: ${colors.purpleIllusion["100"]};
	font-family: "Manrope", sans-serif;
	margin: 0 auto;
	padding: 0rem;
	flex: 1;
	width: 100%;
	${HorizontalContentPadding}
`

const Footer = styled(Box)`
	background-color: ${colors.purpleIllusion["100"]};
	padding: 3rem 0rem;
	padding-top: 2rem;
	font-size: 0.9rem;
	${HorizontalContentPadding}
`
const HeroWrapper = styled(Box)`
	background: ${gradients["brand-gradient-4-90deg"]};
	color: white;
	padding: 2.5rem;
	${HorizontalContentPadding}
`

const HeroHeader = styled(Box)`
	display: flex;
	justify-content: space-between;
	align-items: center;
`

const BalanceText = styled(Box)`
	text-align: center;
	margin-top: 2rem;
`

const Divider = styled("hr")`
	margin: 1rem auto;
	border: 0;
	border-top: 1px solid rgba(255, 255, 255, 0.2);
	width: 60%;
`

export {OutstandingBalance}
